/**
 * `/projects/new` — register a project (`WizardContext.dc.html`, condensed).
 *
 * The artboard is a seven-step wizard. This is one page with the same content, because a
 * stepper adds navigation state without adding information; the step boundaries can be
 * put back once there is something behind each one (GitLab browsing, tooling, schedule).
 *
 * The part that is **not** condensed is the scope panel. It recomputes on every toggle
 * using `isApplicable` from `@portal/domain` — the same function the server runs when it
 * stores the project and when the first audit materialises its items (ADR 0003). If the
 * number here disagreed with the number after saving, the tool would be lying.
 */

import { useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { TEAM_ROLES } from '@portal/contracts';
import type {
  Criticality,
  DataClassification,
  Exposure,
  HostingModel,
  TeamRole_,
  UserSummary,
} from '@portal/contracts';
import { isApplicable, parseContextCondition } from '@portal/domain';
import type { ContextExpr, ContextProfile } from '@portal/domain';
import { Avatar, PermissionButton, WarningGlyph } from '@portal/ui';

import { ApiClientError, api } from '../api.js';

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

const FIELD: CSSProperties = {
  height: 'var(--h-control)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-lg)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  font: '400 12.5px/1 var(--font-sans)',
  padding: '0 10px',
  width: '100%',
};

/** The four flags that decide applicability, and what each one brings in (§6.1). */
const FLAGS: Array<{ key: keyof ContextProfile; label: string; note: string }> = [
  {
    key: 'processesPersonalData',
    label: 'Handles personal data',
    note: 'Brings in the GDPR set: processing record, retention and deletion, data-subject requests, sub-processors.',
  },
  {
    key: 'publiclyExposed',
    label: 'Public-facing / internet-exposed',
    note: 'Requires DAST scanning, TLS and certificate monitoring, external uptime checks, and an annual penetration test.',
  },
  {
    key: 'exposesPublicApi',
    label: 'Exposes an API to external consumers',
    note: 'Requires a versioning strategy and identifiable consumers per version (F-22).',
  },
  {
    key: 'wizInScope',
    label: 'Wiz cloud posture scanning in scope',
    note: 'Cloud misconfiguration scanning with an owner for its findings and an agreed triage cadence.',
  },
];

interface RepositoryDraft {
  gitlabProjectId: string;
  pathWithNamespace: string;
  defaultBranch: string;
  isPrimary: boolean;
}

function Card({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--r-lg)',
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, font: '600 13px/1.3 var(--font-sans)' }}>{title}</h2>
        {note !== undefined && (
          <span style={{ fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 }}>{note}</span>
        )}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={LABEL}>{label}</span>
      {children}
    </label>
  );
}

export function ProjectNewRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const checklist = useQuery({ queryKey: ['checklist', 'current'], queryFn: api.currentChecklist });
  const users = useQuery({ queryKey: ['users'], queryFn: api.users });

  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [criticality, setCriticality] = useState<Criticality>('HIGH');
  const [dataClassification, setDataClassification] = useState<DataClassification>('CONFIDENTIAL');
  const [exposure, setExposure] = useState<Exposure>('INTERNAL');
  const [hostingRegion, setHostingRegion] = useState('West Europe');
  const [gitlabGroupId, setGitlabGroupId] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');

  const [context, setContext] = useState<ContextProfile>({
    processesPersonalData: false,
    publiclyExposed: false,
    exposesPublicApi: false,
    wizInScope: false,
    hostingModel: 'AZURE',
    usesManagedServices: true,
  });

  const [repositories, setRepositories] = useState<RepositoryDraft[]>([
    { gitlabProjectId: '', pathWithNamespace: '', defaultBranch: 'main', isPrimary: true },
  ]);

  const [members, setMembers] = useState<Array<{ userId: string; role: TeamRole_ }>>([]);

  // `retry: false` on both: when GitLab has no token the answer will not change on a
  // second attempt, and the form falls back to manual paths immediately rather than
  // making the user wait through retries.
  const groups = useQuery({
    queryKey: ['gitlab', 'groups'],
    queryFn: () => api.gitlabGroups(''),
    retry: false,
  });

  const groupRepos = useQuery({
    queryKey: ['gitlab', 'projects', selectedGroup],
    queryFn: () => api.gitlabProjects(selectedGroup),
    enabled: selectedGroup !== '',
    retry: false,
  });

  /**
   * Parse each condition once, not on every keystroke: 22 of the 218 items carry one, and
   * this recomputes whenever a toggle changes.
   */
  const parsedItems = useMemo(() => {
    const items = checklist.data?.items ?? [];
    return items.map((item) => ({
      ref: item.ref,
      obligation: item.obligation,
      contextCondition:
        item.contextCondition === null
          ? null
          : (parseContextCondition(item.contextCondition) as ContextExpr),
    }));
  }, [checklist.data]);

  const scope = useMemo(() => {
    let applicable = 0;
    let mandatory = 0;
    let recommended = 0;
    for (const item of parsedItems) {
      if (!isApplicable(item, context)) continue;
      applicable += 1;
      if (item.obligation === 'MANDATORY') mandatory += 1;
      else recommended += 1;
    }
    return {
      applicable,
      mandatory,
      recommended,
      notApplicable: parsedItems.length - applicable,
      total: parsedItems.length,
    };
  }, [parsedItems, context]);

  const create = useMutation({
    mutationFn: api.createProject,
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      navigate(`/projects/${created.id}`);
    },
  });

  const problems: string[] = [];
  if (name.trim().length < 2) problems.push('Give the project a name.');
  if (clientName.trim().length < 2) problems.push('Name the client.');
  if (repositories.filter((repository) => repository.isPrimary).length !== 1) {
    problems.push('Mark exactly one repository as primary.');
  }
  if (repositories.some((repository) => repository.pathWithNamespace.trim() === '')) {
    problems.push('Every repository needs its path.');
  }
  if (members.length === 0) problems.push('Assign at least one person.');

  const submit = (): void => {
    create.mutate({
      name: name.trim(),
      clientName: clientName.trim(),
      criticality,
      dataClassification,
      exposure,
      hostingRegion: hostingRegion.trim(),
      gitlabGroupId:
        selectedGroup !== '' ? selectedGroup : gitlabGroupId.trim() === '' ? null : gitlabGroupId.trim(),
      context,
      repositories: repositories.map((repository) => ({
        ...repository,
        // Until GitLab browsing exists (§15 decision 5) the path doubles as the id, so
        // the probes have something stable to key on.
        gitlabProjectId:
          repository.gitlabProjectId.trim() === ''
            ? repository.pathWithNamespace.trim()
            : repository.gitlabProjectId.trim(),
        pathWithNamespace: repository.pathWithNamespace.trim(),
        defaultBranch: repository.defaultBranch.trim() || 'main',
      })),
      members: members.map((member) => ({ ...member, isBackup: false })),
    });
  };

  if (checklist.isError) {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 640 }}>
        <h1 style={{ margin: '0 0 8px', font: '600 19px/1.2 var(--font-sans)' }}>
          A project cannot be registered yet
        </h1>
        <p style={{ fontSize: 12.5, color: 'var(--mu)', lineHeight: 1.6 }}>
          {checklist.error instanceof ApiClientError
            ? checklist.error.message
            : 'The checklist could not be loaded.'}
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 24px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 760 }}>
        <header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h1 style={{ margin: 0, font: '600 21px/1.1 var(--font-sans)', letterSpacing: '-0.01em' }}>
            Register a project
          </h1>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--mu)' }}>
            Against checklist{' '}
            <span className="mono">v{checklist.data?.version ?? '…'}</span> · the context profile
            decides how many of its {scope.total === 0 ? '218' : scope.total} items apply
          </p>
        </header>

        <Card title="Identity">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <Field label="Project name">
              <input style={FIELD} value={name} onChange={(e) => setName(e.target.value)} placeholder="Aurora Customer Portal" />
            </Field>
            <Field label="Client">
              <input style={FIELD} value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Novaris Group" />
            </Field>
            <Field label="Criticality">
              <select style={FIELD} value={criticality} onChange={(e) => setCriticality(e.target.value as Criticality)}>
                {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </Field>
            <Field label="Data classification">
              <select style={FIELD} value={dataClassification} onChange={(e) => setDataClassification(e.target.value as DataClassification)}>
                {['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </Field>
            <Field label="Exposure">
              <select style={FIELD} value={exposure} onChange={(e) => setExposure(e.target.value as Exposure)}>
                {['INTERNAL', 'PARTNER', 'PUBLIC'].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </Field>
            <Field label="Hosting region">
              <input style={FIELD} value={hostingRegion} onChange={(e) => setHostingRegion(e.target.value)} />
            </Field>
          </div>
        </Card>

        <Card
          title="Repositories"
          note="Pick a GitLab group and tick the repositories, or type a path. Items A-01 and A-26 are answered against the primary one alone."
        >
          {/* Browsing shows what the service token can see, not what you can see — until
              GitLab acts as the calling user (§9.1). Manual entry stays available, and it
              is the fallback whenever the token is missing or a group is unreachable. */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Field label="GitLab group">
                <select
                  style={FIELD}
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  disabled={groups.isError}
                >
                  <option value="">
                    {groups.isPending
                      ? 'Loading groups…'
                      : groups.isError
                        ? 'GitLab unavailable — type paths below'
                        : '— choose a group —'}
                  </option>
                  {(groups.data ?? []).map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.fullPath}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          {groups.isError && (
            <p style={{ margin: 0, fontSize: 11, color: 'var(--wa)', lineHeight: 1.5 }}>
              {groups.error instanceof ApiClientError ? groups.error.message : 'GitLab is unavailable.'}
            </p>
          )}

          {selectedGroup !== '' && (
            <div
              style={{
                border: '1px solid var(--bd)',
                borderRadius: 'var(--r-lg)',
                maxHeight: 220,
                overflow: 'auto',
              }}
            >
              {groupRepos.isPending && (
                <p style={{ margin: 0, padding: 10, fontSize: 12, color: 'var(--mu)' }}>
                  Loading repositories…
                </p>
              )}
              {groupRepos.isError && (
                <p style={{ margin: 0, padding: 10, fontSize: 12, color: 'var(--ba)' }}>
                  {groupRepos.error instanceof ApiClientError
                    ? groupRepos.error.message
                    : 'Could not list repositories.'}
                </p>
              )}
              {(groupRepos.data ?? []).map((repo) => {
                const linked = repositories.some(
                  (entry) => entry.pathWithNamespace === repo.pathWithNamespace,
                );
                return (
                  <label
                    key={repo.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '6px 10px',
                      borderBottom: '1px solid var(--bd)',
                      fontSize: 12,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={linked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRepositories((current) => {
                            const withoutBlanks = current.filter(
                              (entry) => entry.pathWithNamespace.trim() !== '',
                            );
                            return [
                              ...withoutBlanks,
                              {
                                gitlabProjectId: repo.id,
                                pathWithNamespace: repo.pathWithNamespace,
                                defaultBranch: repo.defaultBranch,
                                isPrimary: withoutBlanks.length === 0,
                              },
                            ];
                          });
                        } else {
                          setRepositories((current) => {
                            const remaining = current.filter(
                              (entry) => entry.pathWithNamespace !== repo.pathWithNamespace,
                            );
                            // Never leave the set without a primary.
                            return remaining.some((entry) => entry.isPrimary) || remaining.length === 0
                              ? remaining
                              : remaining.map((entry, index) => ({ ...entry, isPrimary: index === 0 }));
                          });
                        }
                      }}
                    />
                    <span className="mono" style={{ flex: 1 }}>{repo.pathWithNamespace}</span>
                    <span className="mono" style={{ fontSize: 11, color: 'var(--mu)' }}>
                      {repo.defaultBranch}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {repositories.map((repository, index) => (
              <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <Field label={index === 0 ? 'Path with namespace' : ''}>
                    <input
                      style={FIELD}
                      value={repository.pathWithNamespace}
                      placeholder="novaris/aurora-web"
                      onChange={(e) =>
                        setRepositories((current) =>
                          current.map((entry, i) =>
                            i === index ? { ...entry, pathWithNamespace: e.target.value } : entry,
                          ),
                        )
                      }
                    />
                  </Field>
                </div>
                <div style={{ width: 120 }}>
                  <Field label={index === 0 ? 'Default branch' : ''}>
                    <input
                      style={FIELD}
                      value={repository.defaultBranch}
                      onChange={(e) =>
                        setRepositories((current) =>
                          current.map((entry, i) =>
                            i === index ? { ...entry, defaultBranch: e.target.value } : entry,
                          ),
                        )
                      }
                    />
                  </Field>
                </div>
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 6, height: 'var(--h-control)', fontSize: 11.5, color: 'var(--mu)', whiteSpace: 'nowrap' }}
                  title="Items A-01 and A-26 are answered against the primary repository alone"
                >
                  <input
                    type="radio"
                    name="primary"
                    checked={repository.isPrimary}
                    onChange={() =>
                      setRepositories((current) =>
                        current.map((entry, i) => ({ ...entry, isPrimary: i === index })),
                      )
                    }
                  />
                  Primary
                </label>
                {repositories.length > 1 && (
                  <PermissionButton
                    onClick={() => setRepositories((current) => current.filter((_, i) => i !== index))}
                  >
                    Remove
                  </PermissionButton>
                )}
              </div>
            ))}
            <PermissionButton
              onClick={() =>
                setRepositories((current) => [
                  ...current,
                  { gitlabProjectId: '', pathWithNamespace: '', defaultBranch: 'main', isPrimary: false },
                ])
              }
            >
              Add a repository
            </PermissionButton>
          </div>
        </Card>

        <Card
          title="Context profile"
          note="Each flag names the items it governs. A contextual item that does not apply leaves both sides of the score — never counted as a failure."
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {FLAGS.map((flag) => (
              <label
                key={flag.key}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: '10px 11px',
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--r-lg)',
                  background: context[flag.key] === true ? 'var(--acs)' : 'var(--sf)',
                  borderColor: context[flag.key] === true ? 'var(--inbd)' : 'var(--bd)',
                }}
              >
                <input
                  type="checkbox"
                  checked={context[flag.key] === true}
                  onChange={(e) => setContext((current) => ({ ...current, [flag.key]: e.target.checked }))}
                  style={{ marginTop: 2 }}
                />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{flag.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 }}>{flag.note}</span>
                </span>
              </label>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <Field label="Hosting model">
              <select
                style={FIELD}
                value={context.hostingModel}
                onChange={(e) => setContext((c) => ({ ...c, hostingModel: e.target.value as HostingModel }))}
              >
                {['AZURE', 'HYBRID', 'ON_PREM'].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </Field>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'end', height: 'var(--h-control)', fontSize: 12 }}>
              <input
                type="checkbox"
                checked={context.usesManagedServices}
                onChange={(e) => setContext((c) => ({ ...c, usesManagedServices: e.target.checked }))}
              />
              Uses managed services
            </label>
          </div>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 }}>
            Hosting model and managed services are <b>not</b> applicability flags — they select
            which evidence expectation applies to C-01 and C-05 (Azure Monitor or Netdata).
          </p>
        </Card>

        <Card title="Team" note="Who owns which items, and who signs which sign-off row.">
          {users.data === undefined ? (
            <span style={{ fontSize: 12, color: 'var(--mu)' }}>Loading people…</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {TEAM_ROLES.map(({ role, name: roleName }) => {
                const assigned = members.find((member) => member.role === role);
                return (
                  <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="mono" style={{ width: 34, fontSize: 11.5, fontWeight: 600, color: 'var(--mu)' }}>
                      {role}
                    </span>
                    <span style={{ width: 150, fontSize: 12 }}>{roleName}</span>
                    <select
                      style={{ ...FIELD, width: 260 }}
                      value={assigned?.userId ?? ''}
                      onChange={(e) => {
                        const userId = e.target.value;
                        setMembers((current) => {
                          const without = current.filter((member) => member.role !== role);
                          return userId === '' ? without : [...without, { userId, role }];
                        });
                      }}
                    >
                      <option value="">— nobody yet —</option>
                      {(users.data as UserSummary[]).map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.displayName} ({user.orgRole})
                        </option>
                      ))}
                    </select>
                    {assigned !== undefined && (
                      <Avatar
                        initials={
                          (users.data as UserSummary[]).find((u) => u.id === assigned.userId)?.initials ?? '??'
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* The scope panel: the same engine the server will run. */}
      <aside style={{ width: 320, flex: '0 0 320px', display: 'flex', flexDirection: 'column', gap: 12, position: 'sticky', top: 16 }}>
        <div
          style={{
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--r-lg)',
            padding: '16px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <span style={LABEL}>Resulting scope</span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span className="mono" style={{ font: '600 32px/1 var(--font-sans)' }}>{scope.applicable}</span>
            <span style={{ fontSize: 13, color: 'var(--mu)' }}>items in scope</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              ['Mandatory', scope.mandatory],
              ['Recommended', scope.recommended],
              ['Not applicable', scope.notApplicable],
            ].map(([label, value]) => (
              <div key={label as string} style={{ display: 'flex', fontSize: 12 }}>
                <span style={{ flex: 1, color: label === 'Not applicable' ? 'var(--mu)' : undefined }}>{label}</span>
                <span className="mono" style={{ fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--mu)', lineHeight: 1.55, borderTop: '1px solid var(--bd)', paddingTop: 9 }}>
            Recomputed with the same function the server uses, so this number is the one the
            first audit will open with.
          </p>
          {checklist.data !== undefined && checklist.data.missingEvidenceExpectation > 0 && (
            <p style={{ margin: 0, fontSize: 11, color: 'var(--wa)', lineHeight: 1.55 }}>
              {checklist.data.missingEvidenceExpectation} of these items have no evidence
              expectation written yet, so auditors will have nothing quoted to check against.
            </p>
          )}
        </div>

        {problems.length > 0 && (
          <div
            style={{
              background: 'var(--wab)',
              border: '1px solid var(--wabd)',
              borderRadius: 'var(--r-lg)',
              padding: '11px 12px',
              display: 'flex',
              gap: 9,
            }}
          >
            <span style={{ color: 'var(--wa)', marginTop: 1 }}><WarningGlyph size={14} /></span>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, color: 'var(--wa)', lineHeight: 1.6 }}>
              {problems.map((problem) => <li key={problem}>{problem}</li>)}
            </ul>
          </div>
        )}

        {create.isError && (
          <div
            style={{
              background: 'var(--bab)',
              border: '1px solid var(--babd)',
              borderRadius: 'var(--r-lg)',
              padding: '11px 12px',
              fontSize: 11.5,
              color: 'var(--ba)',
              lineHeight: 1.55,
            }}
          >
            {create.error instanceof ApiClientError
              ? create.error.message
              : 'The project could not be created.'}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <PermissionButton onClick={() => navigate('/')}>Cancel</PermissionButton>
          <PermissionButton
            variant="primary"
            onClick={submit}
            disabledReason={
              problems.length > 0
                ? problems[0]
                : create.isPending
                  ? 'Creating…'
                  : null
            }
          >
            {create.isPending ? 'Creating…' : 'Register project'}
          </PermissionButton>
        </div>
      </aside>
    </div>
  );
}
